// views/ATFM.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import openIcon from '../../images/open.svg';

const ATFM = ({ flowMeasures, onFlowMeasuresChange }) => {
  const [showExpired, setShowExpired] = useState(false);
  const [showNotified, setShowNotified] = useState(true);

  const isExpired = (endtime) => new Date().toISOString() > endtime;
  const isNotified = (starttime) => {
    const now = new Date();
    const startDate = new Date(starttime);
    const timeDiff = startDate - now;
    return timeDiff > 0 && timeDiff <= 24 * 60 * 60 * 1000; // 24 hours in milliseconds
  };
  const isWithdrawn = (withdrawn_at) => !!withdrawn_at;

  const formatTime = (time) => time.replace("T", "  ").replace(/:\d{2}\.\d{3}Z$/, "Z").replace(/:\d{2}Z$/, "");
  const formatEndTime = (starttime, endtime) => {
    const startDate = new Date(starttime);
    const endDate = new Date(endtime);
    return startDate.toDateString() === endDate.toDateString()
      ? endtime.split("T")[1].substring(0, 5)
      : endtime.replace("T", " ").substring(0, 16);
  };
  const formatWithdrawnTime = (withdrawn_at) => withdrawn_at.split("T")[1].substring(0, 5);

  const getTimeLeftTooltip = (timestamp, withdrawn_at) => {
    if (withdrawn_at) return "Withdrawn";
    const now = new Date();
    const target = new Date(timestamp);
    const timeDiff = target - now;
    if (timeDiff <= 0) return "Expired";
    const hours = Math.floor(timeDiff / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m left`;
  };

  const getType = (measure) => {
    const typeMapping = {
      minimum_departure_interval: "MDI",
      average_departure_interval: "ADI",
      per_hour: "Rate",
      miles_in_trail: "MIT",
      max_ias: "Max IAS",
      max_mach: "Max Mach",
      ias_reduction: "IAS Reduction",
      mach_reduction: "Mach Reduction",
      prohibit: "Prohibit",
      ground_stop: "GROUND STOP",
      mandatory_route: "Mandatory Route",
    };
    return measure?.type ? typeMapping[measure.type] || "NIL" : "NIL";
  };

  const getFormattedValue = (measure) => {
    if (!measure?.value) return "NIL";
    const { type, value } = measure;
    switch (type) {
      case "minimum_departure_interval":
      case "average_departure_interval":
        const minutes = Math.floor(value / 60);
        const seconds = value % 60;
        return seconds === 0 ? `${minutes} min` : `${minutes} min ${seconds} sec`;
      case "per_hour":
        return `${value} / h`;
      case "miles_in_trail":
        return `${value} nm`;
      case "max_ias":
        return `${value} kt`;
      case "max_mach":
        return `0.${value}`;
      case "ias_reduction":
        return `${value} kt`;
      case "mach_reduction":
        return `0.0${value}`;
      case "prohibit":
        return "Prohibited";
      case "ground_stop":
        return " ";
      case "mandatory_route":
        return measure.value.join(" OR ");
      default:
        return "NIL";
    }
  };

  const formatFilters = (filters) => {
    if (!Array.isArray(filters) || filters.length === 0) return "None";
    return filters
      .map((filter) => {
        const { type, value } = filter;
        switch (type) {
          case "ADEP":
          case "ADES":
            return `${type}: ${Array.isArray(value) ? value.join(", ") : value}`;
          case "level_above":
            return `FL${value}+`;
          case "level_below":
            return `FL${value}-`;
          case "level":
            return `FL=${Array.isArray(value) ? value.join("/") : value}`;
          case "waypoint":
            return `Via: ${Array.isArray(value) ? value.join(" OR ") : value}`;
          case "member_event":
            return "Event Member";
          case "member_not_event":
            return "Not Event Member";
          default:
            return `${type}: ${JSON.stringify(value)}`;
        }
      })
      .join(" | ");
  };

  const filteredMeasures = flowMeasures.filter((measure) => {
    if (!showExpired && isExpired(measure.endtime)) return false;
    if (!showNotified && isNotified(measure.starttime)) return false;
    return true;
  });

  return (
    <div>
      {/* Top Menu */}
      <div className="text-sm bg-gray-400" style={{ display: "flex", gap: "4px", paddingBottom: "8px" }}>
        <button
          onClick={() => setShowNotified(!showNotified)}
          style={{
            backgroundColor: showNotified ? "orange" : "transparent",
            color: showNotified ? "white" : "orange",
            border: "1px solid orange",
            padding: "4px 8px",
          }}
        >
          {showNotified ? "Hide Notified" : "Show Notified"}
        </button>
        <button
          onClick={() => setShowExpired(!showExpired)}
          style={{
            backgroundColor: showExpired ? "red" : "transparent",
            color: showExpired ? "white" : "red",
            border: "1px solid red",
            padding: "4px 8px",
          }}
        >
          {showExpired ? "Hide Expired" : "Show Expired"}
        </button>
        <button
          onClick={() => window.open("https://ecfmp.vatsim.net/dashboard/flow-measures/create", "_blank")}
          style={{ backgroundColor: "#4CAF50", color: "white", border: "none", padding: "4px 8px" }}
          className="flex items-center justify-center gap-2"
        >
          Add Measure
          <img className="w-[16px]" src={openIcon} alt="Open"/>
        </button>
      </div>

      {/* Table */}
      <table style={{ width: "100%", borderCollapse: "collapse", textAlign: 'left'}}>
        <thead>
          <tr className="bg-gray-400 text-white text-sm">
            <th style={{ padding: "2px" }}>ID</th>
            <th style={{ padding: "2px" }}>Start</th>
            <th style={{ padding: "2px" }}>End</th>
            <th style={{ padding: "2px" }}>Type</th>
            <th style={{ padding: "2px" }}>Value</th>
            <th style={{ padding: "2px" }}>Filters</th>
          </tr>
        </thead>
        <tbody className="monospace-font text-xs">
          {filteredMeasures.length > 0 ? (
            filteredMeasures.map((measure) => (
              <tr
                key={measure.ident}
                style={{
                  backgroundColor: isExpired(measure.endtime) ? "#FFCCCB" : isNotified(measure.starttime) ? "#FFD580" : "transparent",
                  border: "1px solid white"
                }}
              >
                <td style={{ padding: "8px" }}>
                  <a
                    href={`https://ecfmp.vatsim.net/dashboard/flow-measures/${measure.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "inherit", textDecoration: "none" }}
                  >
                    {measure.ident}
                  </a>
                </td>
                <td style={{ padding: "8px" }} title={getTimeLeftTooltip(measure.starttime, measure.withdrawn_at)}>
                  {formatTime(measure.starttime)}
                </td>
                <td style={{ padding: "8px" }} title={getTimeLeftTooltip(measure.endtime, measure.withdrawn_at)}>
                  <span style={{ textDecoration: measure.withdrawn_at ? "line-through" : "none" }}>
                    {formatEndTime(measure.starttime, measure.endtime)}
                  </span>
                  {measure.withdrawn_at && <span style={{ marginLeft: "5px" }}>{formatWithdrawnTime(measure.withdrawn_at)}</span>}
                </td>
                <td style={{ padding: "8px" }}>{getType(measure.measure)}</td>
                <td style={{ padding: "8px" }}>{getFormattedValue(measure.measure)}</td>
                <td style={{ padding: "8px" }}>{formatFilters(measure.filters)}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="6" style={{ textAlign: "center", padding: "16px", backgroundColor: "#e0e0e0" }}>
                No Flow Measures
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ATFM;



